<template>
  <VeeForm
    as="div"
    ref="form"
    name="PickupReturnCaseForm"
    :validate-on-mount="true"
    :validation-schema="validationSchema">
    <div class="return-case-form">
      <div class="customer-info">
        <SelectField
          :label="$t('viewCase.return_reason')"
          name="reason"
          :modelValue="returnCase.reason"
          :options="defectReasonOptions"
          :placeholder="$t('global.select_placeholder')"
          :disabled="!isEditable"
          :required="isEditable"
          data-cy="defect-reason-dropdown"
          @update:modelValue="(val) => updateCaseField('reason', val)" />
        <BackstageInput
          :model-value="returnCase.description"
          :label="$t('returnCaseForm.specialInstructions')"
          type="text"
          :max-chars="1000"
          name="description"
          :disabled="!isEditable"
          @update:modelValue="(val) => updateCaseField('description', val as string)" />
        <BackstageInput
          name="addressNotes"
          type="text"
          :max-chars="50"
          :disabled="!isEditable"
          :label="$t('returnCaseForm.addressNotes')"
          :model-value="returnCase.pickup?.addressNotes"
          @update:modelValue="(val) => updatePickupField('addressNotes', val as string)" />
        <BackstageInput
          :model-value="returnCase.pickup?.contact?.name"
          :label="$t('returnCaseForm.contactName')"
          :required="isEditable"
          :disabled="!isEditable"
          type="text"
          :max-chars="50"
          name="name"
          @update:modelValue="(val) => updatePickupContactField('name', val as string)" />
        <div class="phone-number-cnt double-fields">
          <SelectField
            class="phone-code"
            name="countryCode"
            :model-value="callingCountryCode"
            :options="callingCodesOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupCallInfo(val as string)" />
          <BackstageInput
            :model-value="returnCase.pickup?.contact?.phone"
            :disabled="!isEditable"
            :label="$t('returnCaseForm.phoneNumber')"
            :required="isEditable"
            name="phone"
            type="number"
            class="number-field"
            @update:modelValue="(val) => updatePickupContactField('phone', val as string)" />
        </div>
        <BackstageInput
          :model-value="returnCase.pickup?.contact?.email"
          :disabled="!isEditable"
          :label="$t('returnCaseForm.consigneeEmail')"
          :required="isEditable"
          name="email"
          type="text"
          @update:modelValue="(val) => updatePickupContactField('email', val as string)" />
        <BackstageInput
          :model-value="returnCase.pickup?.customerReference"
          :label="$t('returnCaseForm.customerReference')"
          type="text"
          :max-chars="50"
          name="customerReference"
          :disabled="!isEditable"
          @update:modelValue="(val) => updatePickupField('customerReference', val as string)" />
        <SelectField
          :label="$t('returnCaseForm.locationRestricted')"
          name="restrictedAccess"
          :model-value="returnCase.pickup?.restrictedAccess"
          :options="yesNoOptions"
          :disabled="!isEditable"
          @update:modelValue="(val) => updatePickupField('restrictedAccess', Boolean(val))" />
      </div>
      <div class="products-info">
        <SelectField
          :label="$t('returnCaseForm.tailLift')"
          name="tailLiftRequired"
          :model-value="returnCase.pickup?.tailLiftRequired"
          :options="yesNoOptions"
          :disabled="!isEditable"
          @update:modelValue="(val) => updatePickupField('tailLiftRequired', Boolean(val))" />
        <BackstageInputCalendar
          :model-value="returnCase.pickup?.pickupDate"
          class="date-selector"
          name="pickupDate"
          :label="$t('returnCaseForm.collectionDate')"
          :min-date="new Date()"
          :placeholder="$t('global.datepicker_placeholder')"
          :disabled="!isEditable"
          @update:modelValue="(val) => updatePickupField('pickupDate', formatDateToIsoStringIgnoringTime(val))">
          <template #tooltip>
            <span class="has-tooltip" data-cy="tooltip-production-code">
              ¡
              <p class="tooltip">{{ $t('returnCaseForm.datepickerTooltip') }}</p>
            </span>
          </template>
        </BackstageInputCalendar>
        <div class="flex-grid">
          <BackstageInput
            :model-value="returnCase.parcelCount"
            ref="parcelNoInputRef"
            :disabled="!isEditable"
            :label="$t('returnCaseForm.parcels')"
            :required="isEditable"
            type="number"
            name="parcelCount"
            class="flex-grow"
            @update:modelValue="(val) => updateCaseField('parcelCount', val as number)" />
          <BackstageInput
            ref="palletNoInputRef"
            :model-value="returnCase.palletCount"
            :disabled="!isEditable"
            :label="$t('returnCaseForm.pallets')"
            :required="isEditable"
            type="number"
            name="palletCount"
            class="flex-grow"
            @update:modelValue="(val) => updateCaseField('palletCount', val as number)" />
        </div>
        <span v-show="isParcelOrPalletFieldTouched && !hasParcelOrPallet" class="validation-error">
          {{ $t('formsValidations.fields.one_pallet_or_parcel') }}
        </span>
        <div class="double-fields">
          <SelectField
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            name="weightUnit"
            :model-value="returnCase.pickup?.weightUnit"
            :options="weightUnitsOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupField('weightUnit', val)" />
          <BackstageInput
            :model-value="returnCase.pickup?.totalPackageWeight"
            :label="$t('returnCaseForm.parcelsWeight')"
            type="number"
            name="totalPackageWeight"
            class="number-field"
            :required="isEditable"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupField('totalPackageWeight', val as number)" />
        </div>
        <div class="double-fields">
          <SelectField
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            name="weightUnit"
            :model-value="returnCase.pickup?.weightUnit"
            :options="weightUnitsOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupField('weightUnit', val)" />
          <BackstageInput
            :model-value="returnCase.pickup?.singlePackageWeight"
            :label="$t('returnCaseForm.individualParcelWeight')"
            type="number"
            name="singlePackageWeight"
            class="number-field"
            :required="isEditable"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupField('singlePackageWeight', val as number)" />
        </div>
        <div class="double-fields">
          <SelectField
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            name="unit"
            :model-value="returnCase.pickup?.dimensions?.unit"
            :options="lengthUnitsOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupDimensionField('unit', val)" />
          <BackstageInput
            :model-value="returnCase.pickup?.dimensions?.width"
            :label="$t('returnCaseForm.parcelWidth')"
            type="number"
            name="width"
            class="number-field"
            :required="isEditable"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupDimensionField('width', val as number)" />
        </div>
        <div class="double-fields">
          <SelectField
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            name="unit"
            :model-value="returnCase.pickup?.dimensions?.unit"
            :options="lengthUnitsOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupDimensionField('unit', val)" />
          <BackstageInput
            :model-value="returnCase.pickup?.dimensions?.height"
            :label="$t('returnCaseForm.parcelHeight')"
            type="number"
            name="height"
            class="number-field"
            :required="isEditable"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupDimensionField('height', val as number)" />
        </div>
        <div class="double-fields">
          <SelectField
            class="unit-selector"
            :label="$t('returnCaseForm.units')"
            name="unit"
            :model-value="returnCase.pickup?.dimensions?.unit"
            :options="lengthUnitsOptions"
            :disabled="!isEditable"
            @update:modelValue="(val) => updatePickupDimensionField('unit', val)" />
          <BackstageInput
            :model-value="returnCase.pickup?.dimensions?.length"
            :label="$t('returnCaseForm.parcelLength')"
            :required="isEditable"
            :disabled="!isEditable"
            type="number"
            name="length"
            class="number-field"
            @update:modelValue="(val) => updatePickupDimensionField('length', val as number)" />
        </div>
      </div>
    </div>
    <template v-if="isEditable">
      <VeeField
        v-slot="{ meta, field }"
        name="responsibilityAcceptance"
        type="checkbox"
        :value="true"
        :unchecked-value="false">
        <div class="responsibility-checkbox-cnt">
          <input id="responsibility-checkbox" type="checkbox" v-bind="field" :value="true" />
          <label for="responsibility-checkbox">{{ $t('returnCaseForm.responsibilityMessage') }}</label>
        </div>
        <ErrorMessage v-if="meta.touched" name="responsibilityAcceptance" />
      </VeeField>
    </template>
  </VeeForm>
</template>

<script lang="ts" setup>
import { type ICountryCodeItem, codes } from 'country-calling-code';
import { ErrorMessage, Field as VeeField, Form as VeeForm } from 'vee-validate';
import { computed, ref, toRef, watch } from 'vue';

import BackstageInput, { type Exposed as BackstageInputExposed } from '@/components/common/BackstageInput.vue';
import BackstageInputCalendar from '@/components/common/BackstageInputCalendar.vue';
import SelectField, { type SelectFieldOption } from '@/components/common/SelectField.vue';
import { useFormValidation } from '@/composables/form-validation';
import { useFormat } from '@/composables/format';
import { useCaseUpdater } from '@/composables/useCaseUpdater';
import { useForm } from '@/composables/useForm';
import { useUserStore } from '@/stores/user';
import type { ReturnCase } from '@/types/case.interface';

const props = defineProps<{
  isEditable: boolean;
  returnCase: ReturnCase;
  defectReasonOptions: SelectFieldOption[];
}>();

const emit = defineEmits<{
  'update-case': [returnCase: ReturnCase];
  'form-is-valid': [isValid: boolean];
}>();

const userStore = useUserStore();

const { getFormattedCountryCode, formatDateToIsoStringIgnoringTime } = useFormat();
const { yesNoOptions, weightUnitsOptions, lengthUnitsOptions, callingCodesOptions } = useForm();
const {
  updateCaseField,
  updatePickupField,
  updatePickupContactField,
  updatePickupCallInfo,
  updatePickupDimensionField,
} = useCaseUpdater(toRef(props, 'returnCase'), (updatedCase) => onUpdateCase(updatedCase));
const {
  textMaxCharsValidation,
  textRequiredValidation,
  textRequiredMaxCharsValidation,
  emailValidation,
  numberValidationMaxValue,
  numberValidationRequiredMinMaxValue,
  dateValidation,
  textRequiredMinMaxCharsValidation,
  checkboxValidation,
} = useFormValidation();

const form = ref<InstanceType<typeof VeeForm>>();
const parcelNoInputRef = ref<BackstageInputExposed>();
const palletNoInputRef = ref<BackstageInputExposed>();

const validationSchema = computed(() => {
  return {
    reason: textRequiredValidation('return_reason'),
    description: textMaxCharsValidation('comments', 1000),
    phone: textRequiredMinMaxCharsValidation('phone_number', 8, 11),
    name: textRequiredMaxCharsValidation('contact_name', 50),
    email: emailValidation('consignee_email'),
    customerReference: textMaxCharsValidation('customer_reference', 50),
    address_notes: textMaxCharsValidation('address_notes', 50),
    parcelCount: numberValidationMaxValue('parcels', 199),
    palletCount: numberValidationMaxValue('pallets', 199),
    height: numberValidationRequiredMinMaxValue('parcel_height', 0.1, 500000),
    width: numberValidationRequiredMinMaxValue('parcel_width', 0.1, 500000),
    length: numberValidationRequiredMinMaxValue('parcel_length', 0.1, 500000),
    totalPackageWeight: numberValidationRequiredMinMaxValue('parcels_total_weight', 0.1, 500000),
    singlePackageWeight: numberValidationRequiredMinMaxValue('individual_parcel_weight', 0.1, 500000),
    pickupDate: dateValidation('collection_date', { required: true, minDate: new Date() }),
    responsibilityAcceptance: checkboxValidation('responsibility_acceptance'),
  };
});

const isParcelOrPalletFieldTouched = computed(() => {
  return parcelNoInputRef.value?.touched || palletNoInputRef.value?.touched;
});

const hasParcelOrPallet = computed(() => {
  return (props.returnCase.parcelCount ?? 0) + (props.returnCase.palletCount ?? 0) > 0;
});

const hasPickupDate = computed(() => !!props.returnCase.pickup?.pickupDate);

const isFormValid = computed(() => {
  return hasParcelOrPallet.value && hasPickupDate.value;
});

const callingCountryCode = computed(() => {
  if (props.returnCase.pickup?.contact?.countryCode) {
    // Drop the + and pre-select the country code from the list
    return getFormattedCountryCode(
      props.returnCase.pickup.contact.countryCode.substring(1),
      props.returnCase.pickup?.contact?.country,
    );
  }

  // Attempt to pre-select the phone county code wrt account billing country
  let accountCountryCode = codes.find(
    (country) => country.isoCode3 === userStore.selectedAccount?.billingCountry?.toLowerCase(),
  );

  if (!accountCountryCode) {
    // Use USA as default country
    accountCountryCode = codes.find((country) => country.isoCode3 === 'USA') as ICountryCodeItem;
  }

  return getFormattedCountryCode(accountCountryCode.countryCodes[0], accountCountryCode.country);
});

watch(
  () => form.value?.meta?.valid,
  (isValid = false) => {
    emit('form-is-valid', isValid && isFormValid.value);
  },
  { immediate: true },
);

const onUpdateCase = (updatedCase: ReturnCase) => {
  emit('update-case', updatedCase);
};
</script>

<style lang="scss" scoped>
@mixin flex-cnt {
  display: flex;
  gap: 12px;
}

.return-case-form {
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  @include breakpoint(medium) {
    grid-template-columns: repeat(2, 1fr);
  }

  .customer-info {
    @include flex-cnt;
    width: 100%;
    flex-direction: column;

    .phone-number-cnt {
      .phone-code {
        width: 100px;
        height: 58px;
      }
    }
  }

  .products-info {
    @include flex-cnt;
    width: 100%;
    flex-direction: column;

    .date-selector {
      .has-tooltip {
        position: relative;
        display: inline-block;
        color: var(--secondary-text-color);
        background-color: var(--secondary-bg-color);
        border-radius: 50%;
        padding: 0;
        cursor: pointer;
        height: 0.9375rem;
        width: 0.9375rem;
        text-align: center;
        line-height: 0.875rem;
        text-indent: unset;

        &:hover {
          .tooltip {
            display: block;
          }
        }

        .tooltip {
          position: absolute;
          z-index: 9;
          bottom: 100%;
          left: 0;
          display: none;
          transform: translateX(-50%);
          box-shadow: 0 1.25rem 2.5rem -0.875rem rgba(0, 0, 0, 0.25);
          padding: 16px;
          background-color: var(--primary-bg-color);
          color: var(--primary-text-color);
          width: 200px;
        }
      }
    }
  }

  .double-fields {
    @include flex-cnt;

    .unit-selector {
      width: 5.5rem;
    }

    .number-field {
      flex: 1;
    }
  }
}

.responsibility-checkbox-cnt {
  @include flex-cnt;
  align-items: center;
  margin-top: 20px;
  max-width: 820px;

  label {
    text-align: left;
    font-size: 14px;
    font-weight: 400;
  }

  input[type='checkbox'] {
    appearance: none;
    background-color: #fff;
    margin: 0;
    padding: 0 4px;
    width: 22px;
    height: 22px;
    border: 1px solid #000000;
    border-radius: 2px;
    display: grid;
    place-content: center;
  }

  input[type='checkbox']::before {
    content: '';
    width: 14px;
    height: 14px;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    background-color: #000000;
    border-radius: 2px;
  }

  input[type='checkbox']:checked::before {
    transform: scale(1);
  }
}
</style>
